import uniqueId from 'lodash/uniqueId';

import { COPILOT_GLOBAL_SOURCES } from 'constants/copilot';
import type { Suggestion } from 'interfaces/copilot';
import type { Source, CopilotErrorEvent, CopilotEvent } from 'store/entities/copilot/interfaces';

import { TOP_SOURCES_LIMIT } from './constants';

const EMPTY_ARRAY = [];

function createErrorEvent(): CopilotErrorEvent {
  return {
    authorType: 'live-assistant',
    eventId: uniqueId(),
    timestampInMs: Date.now(),
    type: 'error-message',
    authorId: 'live-assistant',
  };
}

interface IBuildGroupedEventsParams {
  groupedEvents: CopilotEvent[][];
  isCopilotError: boolean | null;
}

export function createGroupedEvents({ groupedEvents, isCopilotError }: IBuildGroupedEventsParams): CopilotEvent[][] {
  if (isCopilotError) {
    return [...groupedEvents, [createErrorEvent()]];
  }

  return groupedEvents;
}

export const getSources = (sources?: Source[]): { topSources: Source[]; totalFilteredSources: number } => {
  if (!sources) {
    return {
      topSources: [],
      totalFilteredSources: 0,
    };
  }

  const seenUrls = new Set<string>();
  const filteredSources = sources.filter((source) => {
    if (source.type === 'file' && source.isGlobal) {
      return false;
    }

    if (source.type === 'web_search_annotation') {
      if (seenUrls.has(source.url)) {
        return false;
      }
      seenUrls.add(source.url);
    }

    return true;
  });

  return {
    topSources: filteredSources.slice(0, TOP_SOURCES_LIMIT).map((source) => {
      if (source.type === 'url') {
        const urlObj = new URL(source.url);

        if (COPILOT_GLOBAL_SOURCES.some((source) => source.includes(urlObj.hostname))) {
          urlObj.searchParams.delete('utm_campaign');
          urlObj.searchParams.set('utm_source', 'livechat');
          urlObj.searchParams.set('utm_medium', 'copilot');
        }

        return {
          ...source,
          url: urlObj.toString(),
        };
      }

      return source;
    }),
    totalFilteredSources: filteredSources.length,
  };
};

export function deserializeSuggestionWithShortcuts(suggestions?: Suggestion[]): Required<Suggestion>[] {
  return (
    (suggestions?.filter(({ shortcut }) => !!shortcut) as Required<Suggestion>[]) ||
    (EMPTY_ARRAY as Required<Suggestion>[])
  );
}
